import {
  Breakpoints,
  CTA,
  DesignTokenSizeEnum,
  Divider,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
  styled,
} from '@volkswagen-onehub/components-core';
import { useState } from 'react';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import AccordionUnderstandingPb from '@/modules/features/components/AccordionUnderstandingPb/AccordionUnderstandingPb';
import FeaturesCarousel from '@/modules/features/components/FeaturesCarousel/FeaturesCarousel';
import { formatCurrency } from '@/modules/utils/currency';
import { getCarOptionsData } from '@/modules/utils/storeUtils';
import Accordion from '@/modules/components/Accordion/Accordion';
import Box from '@/modules/components/Box/Box';
import PriceBreakdownTable, {
  PriceBreakdownTableProps,
} from '@/modules/components/PriceBreakdownTable/PriceBreakdownTable';
import { isDesktop } from '@/modules/utils/breakpoints';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import Superscript from '@/modules/components/Superscript/Superscript';
import TyreInformation from '@/modules/features/components/TyreInformation/TyreInformation';
import FeatureOverlay from '@/modules/features/components/FeatureOverlay/FeatureOverlay';
import { ButtonContainer } from '@/modules/pages/Terms';
import { performVWBasicButtonClick } from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import {
  handleAccordionClose,
  handleAccordionOpen,
} from '@/analytics/eventHelpers/accordion';
import { DisclaimerText } from '@/styles';

interface ModelPriceAndFeaturesProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  priceBreakdownData: PriceBreakdownTableProps;
}

function ModelPriceAndFeatures(props: ModelPriceAndFeaturesProps) {
  const { activeConfiguration, priceBreakdownData } = props;
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const [priceBreakdownAccordionOpen, setPriceBreakdownAccordionOpen] =
    useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const isDesktopWidth = useResizeResponsive(isDesktop);
  const { retailers } = useGlobalStore((state) => state.app);

  const totalPriceLabel = contentManager.get('total-price-label');

  const exteriorFeaturesContent =
    activeConfiguration.pdbData?.exterior &&
    activeConfiguration.pdbData?.otherMonoselects
      ? [
          {
            code: activeConfiguration.pdbData.exterior.code,
            header: contentManager.get('exterior-option-paint-title'),
          },
          {
            code: activeConfiguration.pdbData.otherMonoselects.wheels,
            header: contentManager.get('exterior-option-wheels-title'),
            secondaryLink: {
              label: contentManager.get('ctas-tyre-information'),
              modalContent: (
                <TyreInformation configuration={activeConfiguration} />
              ),
            },
          },
        ]
      : [];

  const interiorFeaturesContent =
    activeConfiguration.pdbData?.interior &&
    activeConfiguration.pdbData?.otherMonoselects
      ? [
          {
            code: activeConfiguration.pdbData.interior.upholstery,
            header: contentManager.get('interior-option-upholstery-title'),
          },
          {
            code: activeConfiguration.pdbData.otherMonoselects[
              'steering-wheels'
            ],
            header: contentManager.get('interior-option-steering-wheel-title'),
          },
          {
            code: activeConfiguration.pdbData.otherMonoselects[
              'in-car-entertainment'
            ],
            header: contentManager.get(
              'interior-option-in-car-entertainment-title'
            ),
          },
        ]
      : [];

  const exteriorFeaturesData = exteriorFeaturesContent.map((feature) => {
    return {
      ...getCarOptionsData(activeConfiguration, feature.code, 'pdb'),
      ...feature,
    };
  });

  const interiorFeaturesData = interiorFeaturesContent.map((feature) => {
    const carOptionsData = getCarOptionsData(
      activeConfiguration,
      feature.code,
      'pdb'
    );

    return {
      ...carOptionsData,
      title:
        activeConfiguration.pdbData?.interior?.upholstery === feature.code
          ? activeConfiguration.pdbData?.interior?.title
          : carOptionsData?.title,
      ...feature,
    };
  });

  const analyticsCommonFields = {
    sectionName: SectionNames.SINGLE_PAGE_MODEL_PRICE_AND_FEATURES,
    analyticsManager,
    analyticsBuilder,
    retailers,
    configuration: activeConfiguration,
  };

  const openOverlay = () => {
    setIsOverlayOpen(true);
    performVWBasicButtonClick({
      label: contentManager.get('ctas-standard-features'),
      ...analyticsCommonFields,
    });
  };

  const priceData = priceBreakdownData.prices.filter(
    (item) => item.price && !item.ignore
  );

  const priceLabel = priceData
    .map((item) => item.label.toLowerCase())
    .join(', ')
    .replace(/,([^,]*)$/, ' and$1');

  const createdByRetailerText = priceData.some((item) => item.info)
    ? contentManager.get('total-price-helper-text-optional')
    : null;

  const onToggleAccordion = () => {
    setPriceBreakdownAccordionOpen(!priceBreakdownAccordionOpen);

    if (!priceBreakdownAccordionOpen) {
      handleAccordionOpen(analyticsCommonFields, {
        groupItemName: totalPriceLabel,
        groupItemState: 'open',
      });
    } else {
      handleAccordionClose(analyticsCommonFields, {
        groupItemName: totalPriceLabel,
        groupItemState: 'closed',
      });
    }
  };

  const PriceWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: 32px;

    @media screen and (max-width: 767px) {
      flex-direction: column;
      gap: 10px;

      > span {
        display: flex;
        justify-content: space-between;
      }
    }
  `;

  return (
    <>
      <Box mb={DesignTokenSizeEnum.dynamic0200}>
        <Box mb={DesignTokenSizeEnum.dynamic0200}>
          <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline400}>
            {contentManager.get('price-and-features-section-title-normal')}
            <strong>
              {' '}
              {contentManager.get('price-and-features-section-title-bold')}
            </strong>
          </Text>
        </Box>

        <Box
          b960={{
            display: 'flex',
            alignItems: 'baseline',
          }}
          gap={DesignTokenSizeEnum.static200}
          mb={DesignTokenSizeEnum.dynamic0100}
        >
          <Text appearance={TokenTextAppearance.headline300} bold>
            {activeConfiguration.pdbData?.derivative?.modelTitle}{' '}
            {activeConfiguration.pdbData?.derivative?.trimTitle}
          </Text>
          <Text appearance={TokenTextAppearance.bigcopy100}>
            {activeConfiguration.pdbData?.derivative?.title}
          </Text>
        </Box>
        <Divider />
        <Accordion
          id="total-price"
          title={
            <PriceWrapper>
              <Text appearance={TokenTextAppearance.bigcopy200}>
                <span>
                  {totalPriceLabel}
                  <Superscript />{' '}
                </span>
                <strong>
                  {formatCurrency(
                    activeConfiguration.pdbData?.pricing?.outstandingTotal
                  )}
                </strong>
              </Text>

              {activeConfiguration.pdbData?.pricing?.p11dPrice &&
                activeConfiguration.pdbData?.fleetMode && (
                  <Text appearance={TokenTextAppearance.bigcopy200}>
                    <span>{contentManager.get('p11d-price-label')} </span>
                    <strong>
                      {formatCurrency(
                        activeConfiguration.pdbData?.pricing?.p11dPrice
                      )}
                    </strong>
                  </Text>
                )}
            </PriceWrapper>
          }
          subtitle={
            priceLabel
              ? contentManager.getParsedString('total-price-helper-text', [
                  {
                    identifier: 'priceLabels',
                    value: [priceLabel, createdByRetailerText].join(' '),
                  },
                ])
              : ''
          }
          isOpen={priceBreakdownAccordionOpen}
          onToggle={onToggleAccordion}
        >
          <PriceBreakdownTable
            prices={priceBreakdownData.prices}
            alignDesktop="start"
          />
        </Accordion>
        <DisclaimerText appearance={TokenTextAppearance.copy100}>
          {contentManager.get('prices-disclaimer-text')}
        </DisclaimerText>
        <Box
          mt={DesignTokenSizeEnum.static200}
          mb={DesignTokenSizeEnum.static370}
          b960={{
            mb: DesignTokenSizeEnum.static520,
          }}
        >
          <AccordionUnderstandingPb
            sectionName={SectionNames.SINGLE_PAGE_MODEL_PRICE_AND_FEATURES}
            configurations={[activeConfiguration]}
          />
        </Box>
      </Box>
      {/* Key Features Section */}
      <Box
        b960={{ mb: DesignTokenSizeEnum.static515 }}
        mb={DesignTokenSizeEnum.static350}
      >
        <Box gap={DesignTokenSizeEnum.static100}>
          <Text
            tag={TextTag.h3}
            appearance={TokenTextAppearance.headline300}
            bold
          >
            {contentManager.get('key-features-section-title')}
          </Text>
        </Box>
        <Box
          mt={DesignTokenSizeEnum.static200}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box b960={{ width: '65%' }}>
            <Text tag={TextTag.p} appearance={TokenTextAppearance.bigcopy100}>
              {contentManager.get('key-features-section-text')}
            </Text>
          </Box>

          {isDesktopWidth && (
            <div>
              <CTA tag="button" emphasis="secondary" onClick={openOverlay}>
                {contentManager.get('ctas-standard-features')}
              </CTA>
            </div>
          )}
          {isOverlayOpen && (
            <FeatureOverlay
              activeConfiguration={activeConfiguration}
              handleClose={() => setIsOverlayOpen(false)}
              sectionName={SectionNames.SINGLE_PAGE_MODEL_PRICE_AND_FEATURES}
            />
          )}
        </Box>
      </Box>
      <Box
        b960={{ mb: DesignTokenSizeEnum.static515 }}
        mb={DesignTokenSizeEnum.static350}
      >
        <Text tag={TextTag.h4} appearance={TokenTextAppearance.bigcopy200}>
          {contentManager.get('your-exterior-section-title-normal')}
          <strong>
            {' '}
            {contentManager.get('your-exterior-section-title-bold')}
          </strong>
        </Text>
        <FeaturesCarousel
          id="exterior-features-carousel"
          ariaLabel="Exterior features carousel"
          featuresData={exteriorFeaturesData}
          slidesPerPage={{
            [Breakpoints.default]: 1,
            [Breakpoints.b960]: 2,
          }}
          hideNavigationDesktop
          sectionName={SectionNames.SINGLE_PAGE_EXTERIOR_FEATURES}
        />
      </Box>

      <Text tag={TextTag.h4} appearance={TokenTextAppearance.bigcopy200}>
        {contentManager.get('your-interior-section-title-normal')}
        <strong>
          {' '}
          {contentManager.get('your-interior-section-title-bold')}
        </strong>
      </Text>
      <FeaturesCarousel
        id="interior-features-carousel"
        ariaLabel="Interior features carousel"
        featuresData={interiorFeaturesData}
        slidesPerPage={{
          [Breakpoints.default]: 1,
          [Breakpoints.b960]: 3,
        }}
        hideNavigationDesktop
        footerStacked
        sectionName={SectionNames.SINGLE_PAGE_INTERIOR_FEATURES}
      />

      <DisclaimerText>
        <Text
          color={TokenTextColor.inherit}
          appearance={TokenTextAppearance.copy100}
        >
          {contentManager.get('prices-disclaimer-text')}
        </Text>
      </DisclaimerText>
      <Box
        mt={DesignTokenSizeEnum.static350}
        b960={{ mt: DesignTokenSizeEnum.static515 }}
      >
        <ButtonContainer>
          <CTA tag="button" emphasis="primary" onClick={openOverlay}>
            {contentManager.get('ctas-standard-features')}
          </CTA>
        </ButtonContainer>
      </Box>
    </>
  );
}

export default ModelPriceAndFeatures;
